import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  useGetActiveGradingConfigQuery,
  useCreateGradingConfigMutation,
  useResetGradingConfigToDefaultMutation,
  GradingConfig,
  PerformanceRatingEntry,
  BonusThresholdEntry,
} from '../../redux/gradingConfigApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface GradingConfigDialogProps {
  open: boolean;
  onClose: () => void;
}

// Default Performance Ratings (matching backend defaults)
const DEFAULT_PERFORMANCE_RATINGS: PerformanceRatingEntry[] = [
  { grade: 'A+', minPercentage: 120, maxPercentage: 999, description: 'Exceptional - Far Exceeds Expectations', colorCode: '#033e79' },
  { grade: 'A', minPercentage: 110, maxPercentage: 119.99, description: 'Outstanding - Exceeds Expectations', colorCode: '#033e79' },
  { grade: 'A-', minPercentage: 105, maxPercentage: 109.99, description: 'Excellent', colorCode: '#17A2B8' },
  { grade: 'B+', minPercentage: 100, maxPercentage: 104.99, description: 'Very Good - Meets All Expectations', colorCode: '#17A2B8' },
  { grade: 'B', minPercentage: 95, maxPercentage: 99.99, description: 'Good - Meets Most Expectations', colorCode: '#17A2B8' },
  { grade: 'C', minPercentage: 89.5, maxPercentage: 94.99, description: 'Satisfactory - Meets Some Expectations', colorCode: '#32CD32' },
  { grade: 'C-', minPercentage: 85, maxPercentage: 89.49, description: 'Below Average', colorCode: '#32CD32' },
  { grade: 'D', minPercentage: 70, maxPercentage: 84.99, description: 'Poor - Needs Improvement', colorCode: '#FFC107' },
  { grade: 'E', minPercentage: 60, maxPercentage: 69.99, description: 'Very Poor', colorCode: '#FFC107' },
  { grade: 'F', minPercentage: 0, maxPercentage: 59.99, description: 'Unacceptable', colorCode: '#DC3545' },
];

// Default Bonus Thresholds (matching backend defaults)
const DEFAULT_BONUS_THRESHOLDS: BonusThresholdEntry[] = [
  { minPercentage: 110, maxPercentage: 999, bonusEntitlement: 100 },
  { minPercentage: 100.1, maxPercentage: 109.99, bonusEntitlement: 75 },
  { minPercentage: 89.5, maxPercentage: 100, bonusEntitlement: 50 },
  { minPercentage: 0, maxPercentage: 89.49, bonusEntitlement: 0 },
];

const GradingConfigDialog: React.FC<GradingConfigDialogProps> = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [performanceRatings, setPerformanceRatings] = useState<PerformanceRatingEntry[]>(DEFAULT_PERFORMANCE_RATINGS);
  const [bonusThresholds, setBonusThresholds] = useState<BonusThresholdEntry[]>(DEFAULT_BONUS_THRESHOLDS);
  const [hasChanges, setHasChanges] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { data: activeConfig, isLoading, error, refetch } = useGetActiveGradingConfigQuery();
  const [createConfig, { isLoading: isSaving }] = useCreateGradingConfigMutation();
  const [resetToDefault, { isLoading: isResetting }] = useResetGradingConfigToDefaultMutation();

  // Load config into state when fetched, or use defaults if API fails
  useEffect(() => {
    if (error) {
      console.error('Failed to fetch grading config:', error);
      setApiError('Could not load saved configuration. Showing defaults.');
      setPerformanceRatings(DEFAULT_PERFORMANCE_RATINGS);
      setBonusThresholds(DEFAULT_BONUS_THRESHOLDS);
      return;
    }
    if (activeConfig) {
      setPerformanceRatings(activeConfig.performanceRatings?.length ? activeConfig.performanceRatings : DEFAULT_PERFORMANCE_RATINGS);
      setBonusThresholds(activeConfig.bonusThresholds?.length ? activeConfig.bonusThresholds : DEFAULT_BONUS_THRESHOLDS);
      setApiError(null);
      setHasChanges(false);
    }
  }, [activeConfig]);

  const handlePerformanceRatingChange = (index: number, field: keyof PerformanceRatingEntry, value: any) => {
    const updated = [...performanceRatings];
    updated[index] = { ...updated[index], [field]: value };
    setPerformanceRatings(updated);
    setHasChanges(true);
  };

  const handleBonusThresholdChange = (index: number, field: keyof BonusThresholdEntry, value: any) => {
    const updated = [...bonusThresholds];
    updated[index] = { ...updated[index], [field]: value };
    setBonusThresholds(updated);
    setHasChanges(true);
  };

  const handleLoadDefaults = () => {
    setPerformanceRatings(DEFAULT_PERFORMANCE_RATINGS);
    setBonusThresholds(DEFAULT_BONUS_THRESHOLDS);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await createConfig({
        configType: 'custom',
        name: 'Custom BSC Grading',
        performanceRatings,
        bonusThresholds,
        isActive: true,
      }).unwrap();
      setHasChanges(false);
      setApiError(null);
      alert('Grading configuration saved successfully');
      refetch();
    } catch (error) {
      console.error('Error saving grading config:', error);
      alert('Error saving configuration. Changes are only local until saved.');
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset to default configuration? This will overwrite any custom settings.')) {
      return;
    }
    try {
      await resetToDefault().unwrap();
      setHasChanges(false);
      alert('Reset to default configuration');
      refetch();
    } catch (error) {
      console.error('Error resetting config:', error);
      alert('Error resetting configuration');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <SettingsIcon color="primary" />
            <Typography variant="h6">BSC Grading Configuration</Typography>
            {activeConfig?.configType === 'custom' && (
              <Chip label="Custom" size="small" color="warning" />
            )}
            {hasChanges && (
              <Chip label="Unsaved Changes" size="small" color="error" variant="outlined" />
            )}
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {apiError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {apiError}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              Configure the grading scale and bonus entitlement thresholds used across all BSC evaluations.
              Changes will affect how grades and bonuses are calculated company-wide.
            </Alert>

            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
              <Tab label="Performance Grades" />
              <Tab label="Bonus Entitlements" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Define the grade boundaries based on BSC total score percentage.
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell width={80}>Grade</TableCell>
                      <TableCell width={120}>Min %</TableCell>
                      <TableCell width={120}>Max %</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell width={100}>Color</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceRatings.map((rating, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={rating.grade}
                            onChange={(e) => handlePerformanceRatingChange(index, 'grade', e.target.value)}
                            sx={{ width: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={rating.minPercentage}
                            onChange={(e) => handlePerformanceRatingChange(index, 'minPercentage', parseFloat(e.target.value))}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={rating.maxPercentage}
                            onChange={(e) => handlePerformanceRatingChange(index, 'maxPercentage', parseFloat(e.target.value))}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={rating.description || ''}
                            onChange={(e) => handlePerformanceRatingChange(index, 'description', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            type="color"
                            value={rating.colorCode || '#000000'}
                            onChange={(e) => handlePerformanceRatingChange(index, 'colorCode', e.target.value)}
                            style={{ width: 50, height: 30, border: 'none', cursor: 'pointer' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Define the bonus entitlement percentage based on BSC total score thresholds.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Current thresholds: {bonusThresholds
                  .slice()
                  .sort((a, b) => a.minPercentage - b.minPercentage)
                  .map((t, i) => {
                    if (t.minPercentage === 0) return `≤${t.maxPercentage}% = ${t.bonusEntitlement}%`;
                    if (t.maxPercentage >= 999) return `≥${t.minPercentage}% = ${t.bonusEntitlement}%`;
                    return `${t.minPercentage}-${t.maxPercentage}% = ${t.bonusEntitlement}%`;
                  })
                  .join(', ')}
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell width={150}>Min Score %</TableCell>
                      <TableCell width={150}>Max Score %</TableCell>
                      <TableCell>Bonus Entitlement %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bonusThresholds.map((threshold, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={threshold.minPercentage}
                            onChange={(e) => handleBonusThresholdChange(index, 'minPercentage', parseFloat(e.target.value))}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={threshold.maxPercentage}
                            onChange={(e) => handleBonusThresholdChange(index, 'maxPercentage', parseFloat(e.target.value))}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={threshold.bonusEntitlement}
                            onChange={(e) => handleBonusThresholdChange(index, 'bonusEntitlement', parseFloat(e.target.value))}
                            InputProps={{
                              endAdornment: <Typography>%</Typography>,
                            }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Tooltip title="Load the default grading scale and bonus thresholds into the form">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleLoadDefaults}
          >
            Load Defaults
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GradingConfigDialog;
